import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ActivityIcon } from 'lucide-react';
import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/page-header';
import { Stat } from '@/components/stat';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { humanise, formatMoney } from '@/components/admin/licence-status-chip';
import { analyticsQuery } from '@/lib/api/admin';
import { messageForError } from '@/lib/api-client';
import { getAuthState } from '@/lib/auth-store';

function AdminAnalyticsPage() {
  const { user } = getAuthState();
  const query = useQuery(analyticsQuery());
  const data = query.data;
  const loading = query.isPending;

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Platform"
        title="Analytics"
        description="Cross-tenant health for the SiteFlow platform: signups, active organisations, licence pressure and publish volume."
        actions={<Badge variant="platform">{user?.platformRole ?? 'staff'}</Badge>}
      />

      {query.isError ? (
        <EmptyState
          icon={ActivityIcon}
          title="Analytics could not be loaded"
          description={messageForError(query.error)}
        />
      ) : (
        <>
          <section aria-labelledby="customers-heading" className="flex flex-col gap-4">
            <h2 id="customers-heading" className="section-label">
              Customers
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Organisations" value={data?.customers.total} loading={loading} />
              <Stat
                label="With live licence"
                value={data?.customers.active}
                loading={loading}
                hint="active or trial"
              />
              <Stat
                label="New, last 30 days"
                value={data?.customers.createdLast30Days}
                loading={loading}
              />
              <Stat
                label="MRR (estimate)"
                value={data ? formatMoney(data.revenue.monthlyRecurring, data.revenue.currency) : null}
                loading={loading}
                hint={data?.revenue.note}
              />
            </div>
          </section>

          <section aria-labelledby="licence-heading" className="flex flex-col gap-4">
            <h2 id="licence-heading" className="section-label">
              Licence pressure
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <Stat
                label="Slot utilisation"
                value={data ? `${Math.round(data.slots.utilisation * 100)}%` : null}
                loading={loading}
                hint={data ? `${data.slots.used} of ${data.slots.purchased} slots` : undefined}
              />
              <Stat
                label="Expiring in 30 days"
                value={data?.licences.expiringWithin30Days}
                loading={loading}
                tone={data && data.licences.expiringWithin30Days > 0 ? 'warning' : 'default'}
              />
              <Stat label="Trials" value={data?.licences.trials} loading={loading} />
              <Stat
                label="Subscriptions past due"
                value={data?.subscriptions.pastDue}
                loading={loading}
                tone={data && data.subscriptions.pastDue > 0 ? 'danger' : 'default'}
              />
            </div>
            {data ? (
              <Card>
                <CardHeader>
                  <CardTitle>Licences by status</CardTitle>
                  <CardDescription>
                    Open any status in the{' '}
                    <Link to="/admin/licences" className="text-accent underline-offset-4 hover:underline">
                      licence list
                    </Link>
                    .
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <dl className="flex flex-wrap gap-x-8 gap-y-3">
                    {Object.entries(data.licences.byStatus).map(([status, value]) => (
                      <div key={status} className="flex flex-col">
                        <dt className="text-micro text-ink-subtle">{humanise(status)}</dt>
                        <dd className="text-lede font-semibold" data-numeric>
                          {value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </CardContent>
              </Card>
            ) : null}
          </section>

          <section aria-labelledby="usage-heading" className="flex flex-col gap-4">
            <h2 id="usage-heading" className="section-label">
              Usage and security
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Active sites" value={data?.content.sitesActive} loading={loading} />
              <Stat
                label="Publishes, 7 days"
                value={data?.content.publishesLast7Days}
                loading={loading}
              />
              <Stat
                label="Failed validations, 24h"
                value={data?.security.failedValidations24h}
                loading={loading}
                tone={data && data.security.failedValidations24h > 0 ? 'warning' : 'default'}
              />
              <Stat
                label="Suspicious activations, 7d"
                value={data?.security.suspiciousActivations7d}
                loading={loading}
                tone={data && data.security.suspiciousActivations7d > 0 ? 'danger' : 'default'}
                hint={data ? `${data.security.lockedAccounts} locked accounts` : undefined}
              />
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/')({
  component: AdminAnalyticsPage,
});
